import React from 'react';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/styles';
import { getPartner } from '../../../../../../lib/access.es6';

const useStyles = makeStyles(() => ({
  dependantWrapper: {
    '& table': {
      width: '100%',
      background: '#FFFFFF',
      border: '1px solid #A7A7A7',
      marginBottom: '20px',
      '& tr': {
        borderTop: '1px solid #A7A7A7',
      },
      '& th': {
        fontFamily: 'Lato',
        fontStyle: 'normal',
        fontWeight: 700,
        fontSize: '12px',
        lineHeight: '15px',
        color: '#000000',
        padding: '10px',
        borderRight: '1px solid #A7A7A7',
      },
      '& td': {
        fontFamily: 'open sans',
        fontStyle: 'normal',
        fontWeight: 400,
        fontSize: '12px',
        lineHeight: '15px',
        color: '#000000',
        padding: '10px',
        borderRight: '1px solid #A7A7A7',
      }
    }
  }
}));

/**
 * Shared component for displaying member benefits tables
 * Used by ManageBenefits and ManageMembers amendments
 *
 * @param {Array} members - List of members to display
 * @param {String} title - Section title
 * @param {Function} renderActionButton - Function to render action button for each member
 * @param {Boolean} showTotal - Whether to show total premium
 * @param {Number} totalPremium - Total premium amount in cents
 * @param {Object} sectionStyle - Optional additional styles for the section
 * @param {Function} renderMemberName - Optional function to customize member name rendering
 */
const MemberBenefitsTable = ({
  members,
  title,
  renderActionButton,
  showTotal = false,
  totalPremium = 0,
  sectionStyle = {},
  renderMemberName = null
}) => {
  const classes = useStyles();
  const currencyCode = getPartner().country.currency_code;

  if (!members || members.length === 0) {
    return null;
  }

  return (
    <section style={{ marginBottom: '30px', ...sectionStyle }}>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <h6>{title}</h6>
        </Grid>
        <Grid item xs={6} style={{ textAlign: 'right', marginTop: '2px' }}>
          {showTotal && (
            <h6 style={{ textAlign: 'right' }}>
              Total Premium: {currencyCode} {(totalPremium / 100).toFixed(2)}
            </h6>
          )}
        </Grid>

        <Grid item xs={12} sm={12} md={12}>
          <div className={classes.dependantWrapper}>
            <table>
              <thead>
                <tr>
                  <th>Member Name</th>
                  <th>Relationship</th>
                  <th>Benefits</th>
                  <th>Premium</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member, index) => (
                  <tr key={member.uuid || index}>
                    <td>
                      {renderMemberName
                        ? renderMemberName(member)
                        : `${member.first_name} ${member.last_name}`
                      }
                    </td>
                    <td>{member.relationship}</td>
                    <td>
                      <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
                        {member.benefits && member.benefits.map((benefit, i) => {
                          // Premium can be in multiple locations depending on benefit type
                          const premiumAmount = benefit.premium_amount
                            || benefit.premium_amount_in_cents
                            || (benefit.additional_details && benefit.additional_details.premium_amount)
                            || 0;

                          return (
                            <li key={i} style={{ marginBottom: '4px' }}>
                              {benefit.type ? benefit.type.toLowerCase() : benefit.name}{' '}
                              <strong>
                                {currencyCode} {((benefit.cover_amount_in_cents || benefit.benefit_amount_in_cents || 0) / 100).toLocaleString()}
                                {' '}({currencyCode} {(premiumAmount / 100).toFixed(2)})
                              </strong>
                            </li>
                          );
                        })}
                      </ul>
                    </td>
                    <td style={{ textAlign: 'right', fontWeight: '600' }}>
                      {currencyCode} {((member.premium_amount_in_cents || 0) / 100).toFixed(2)}
                    </td>
                    <td>
                      {renderActionButton && renderActionButton(member)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Grid>
      </Grid>
    </section>
  );
};

export default MemberBenefitsTable;
